import { RISK_ALERT_KINDS, RISK_IDENTIFICATION_STATUSES, RISK_NETWORKS, RISK_SOURCES, } from '@wit-support/ui-domain';
import { RISK_CASE_REACTION_STATUSES } from './reaction-labels.js';
/**
 * Ключ, под которым живут сохранённые фильтры списка кейсов.
 *
 * Экспортируется наружу модулем: шелл админки обязан стереть его при выходе
 * оператора, иначе следующий человек за той же машиной откроет раздел уже
 * суженным чужим поиском (номер кейса и последние 4 цифры карты — это ещё и
 * след чужой работы). Тот же приём, что у инбокса (`INBOX_FILTERS_KEY`).
 */
export const CHARGEBACKS_FILTERS_KEY = 'wit-admin:chargebacks-filters';
/** Значения фильтров-перечислений, которые сервер вообще понимает. */
const SOURCES = new Set(RISK_SOURCES);
const NETWORKS = new Set(RISK_NETWORKS);
const KINDS = new Set(RISK_ALERT_KINDS);
const IDENTIFICATION_STATUSES = new Set(RISK_IDENTIFICATION_STATUSES);
const REACTION_STATUSES = new Set(RISK_CASE_REACTION_STATUSES);
/** Строка — законная ISO-дата (а не «вчера», не мусор и не «Invalid Date»). */
function validIso(v) {
    return typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : undefined;
}
/** Целые минорные единицы. Ноль законен («от 0»), дробь и NaN — нет. */
function validCents(v) {
    return typeof v === 'number' && Number.isSafeInteger(v) ? v : undefined;
}
function validEnum(v, allowed) {
    return typeof v === 'string' && allowed.has(v) ? v : undefined;
}
/**
 * Читает сохранённые фильтры, откатываясь к пустым (= «показать всё») на любой
 * ошибке, отсутствии и несовпадении формы. Никогда не бросает — годится как
 * ленивый инициализатор `useState`.
 *
 * Каждое поле проверяется ОТДЕЛЬНО и по списку допустимых значений, а не
 * приводится типом вслепую. Причина ровно одна: протухшее значение (сеть
 * переименовали, статус идентификации убрали из таксономии, хранилище правили
 * руками) не должно доехать ни до `Select`, которому нечего показать, ни до
 * сервера, который ответит 400 на неизвестный enum. Такое значение молча
 * выбрасывается — фильтр возвращается в «Все», и выдача честно не сужена.
 *
 * Курсор пагинации не сохраняется вовсе: он принадлежит конкретной выборке в
 * конкретный момент, и возвращаться надо на первую страницу тех же фильтров, а
 * не на страницу, которой уже может не быть.
 */
export function readCaseListFilters() {
    try {
        const raw = localStorage.getItem(CHARGEBACKS_FILTERS_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
            return {};
        const o = parsed;
        const clean = {};
        const source = validEnum(o.source, SOURCES);
        if (source)
            clean.source = source;
        const network = validEnum(o.network, NETWORKS);
        if (network)
            clean.network = network;
        const kind = validEnum(o.kind, KINDS);
        if (kind)
            clean.kind = kind;
        const identificationStatus = validEnum(o.identificationStatus, IDENTIFICATION_STATUSES);
        if (identificationStatus)
            clean.identificationStatus = identificationStatus;
        const reactionStatus = validEnum(o.reactionStatus, REACTION_STATUSES);
        if (reactionStatus)
            clean.reactionStatus = reactionStatus;
        // Только `true`: `false` сервер понимает как ПРОТИВОПОЛОЖНУЮ очередь, а
        // выключенный переключатель обязан не сужать выборку вообще.
        if (o.actionRequired === true)
            clean.actionRequired = true;
        const from = validIso(o.from);
        if (from)
            clean.from = from;
        const to = validIso(o.to);
        if (to)
            clean.to = to;
        const amountFrom = validCents(o.amountFrom);
        if (amountFrom !== undefined)
            clean.amountFrom = amountFrom;
        const amountTo = validCents(o.amountTo);
        if (amountTo !== undefined)
            clean.amountTo = amountTo;
        if (typeof o.q === 'string' && o.q !== '')
            clean.q = o.q;
        return clean;
    }
    catch {
        return {};
    }
}
/** Сохраняет фильтры. Лучшее усилие: отказ хранилища не ломает отрисовку. */
export function writeCaseListFilters(filters) {
    try {
        localStorage.setItem(CHARGEBACKS_FILTERS_KEY, JSON.stringify(filters));
    }
    catch {
        /* приватный режим / переполненное хранилище — не наша беда */
    }
}
/**
 * Есть ли хоть один действующий фильтр.
 *
 * Нужно, чтобы пустая выдача объяснялась правильно: «кейсов нет» и «всё
 * отсеяно сохранённым фильтром» — разные сообщения, и второе обязано нести
 * кнопку сброса, иначе сохранение фильтров превращается в ловушку.
 */
export function hasActiveCaseFilters(f) {
    return Object.values(f).some((v) => v !== undefined);
}
/**
 * Целые минорные единицы обратно в текст поля («13938» → «139.38»).
 *
 * Ровно обратна `parseAmountToCents` в том смысле, который здесь важен: строка
 * разбирается назад в то же число. Хвостовые нули не дописываются — оператор
 * ввёл «100», и увидеть он должен «100», а не «100.00».
 */
export function centsToAmountInput(cents) {
    return cents === undefined ? '' : String(cents / 100);
}
/**
 * ISO-начало периода обратно в дату календаря.
 *
 * Границы уходят в фильтр как UTC-полночь, собранная из КАЛЕНДАРНЫХ полей
 * выбранного дня (см. `toIsoStart`), поэтому и обратно берутся UTC-поля: через
 * локальные `getDate()` к западу от Гринвича день уезжал бы на сутки назад.
 */
export function isoToRangeDay(iso) {
    if (!iso)
        return undefined;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return undefined;
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}
/**
 * ISO-конец периода (ИСКЛЮЧАЮЩИЙ, см. `toIsoEndExclusive`) обратно в дату
 * календаря: минус сутки, иначе в пикере подсветился бы лишний день.
 */
export function isoEndExclusiveToRangeDay(iso) {
    const d = isoToRangeDay(iso);
    return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1) : undefined;
}
//# sourceMappingURL=filters-storage.js.map